import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { RefreshCw, ShieldCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Admin panel: verifies stored standings match a fresh rebuild from completed matches of the active season.
export const StandingsIntegrityPanel = () => {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/admin/standings/integrity`, { withCredentials: true })
    .then(r => setReport(r.data))
    .catch(() => toast.error("Could not load standings check"));

  useEffect(() => { load(); }, []);

  const recalc = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/standings/recalculate`, {}, { withCredentials: true });
      toast.success("Standings rebuilt from completed matches");
      await load();
    } catch {
      toast.error("Recalculation failed");
    } finally {
      setBusy(false);
    }
  };

  if (!report) return null;
  const ok = report.all_in_sync;

  return (
    <div className="bg-white p-6 border border-gray-200" data-testid="standings-integrity-panel">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          {ok ? <ShieldCheck className="w-6 h-6 text-[#009E60]" /> : <AlertTriangle className="w-6 h-6 text-orange-500" />}
          <div>
            <h3 className="font-heading font-bold text-xl uppercase text-[#09090b]">Standings Integrity</h3>
            <p className="text-sm text-gray-500" data-testid="standings-integrity-status">
              {ok ? "All tables match the completed matches of the active season." : "Some tables differ from a fresh rebuild — recalculate to fix."}
            </p>
          </div>
        </div>
        <Button onClick={recalc} disabled={busy} className="bg-[#09090b] hover:bg-black text-white rounded-none" data-testid="recalculate-standings-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} /> Recalculate
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {report.competitions.map(c => (
          <div key={c.competition_id} className={`p-3 border-l-4 bg-gray-50 ${c.in_sync ? "border-[#009E60]" : "border-orange-500"}`} data-testid={`integrity-${c.competition_id}`}>
            <p className="font-medium text-sm">{c.name} <span className="text-gray-400">{c.season}</span></p>
            <p className="text-xs text-gray-500">
              {c.teams} teams • {c.completed_matches} completed matches counted
              {c.uncounted_matches > 0 && ` • ${c.uncounted_matches} not yet played`}
              {c.ignored_other_season_matches > 0 && ` • ${c.ignored_other_season_matches} other-season ignored`}
            </p>
            {!c.in_sync && <p className="text-xs text-orange-600 font-medium mt-1">{c.mismatches.length} row(s) out of sync</p>}
          </div>
        ))}
      </div>
    </div>
  );
};
